import {Alert, Box} from '@mui/material';
import {ChangeEvent, FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {useCreatePermissionMutation, useUpdatePermissionMutation} from 'src/api';
import {AMPInput} from 'src/components/common/AMPInput';
import AMPModal from 'src/components/common/AMPModal';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {actionsWrapperSx, formSx} from 'src/components/forms/AddReManager/AddReManager.sx';
import {PermissionDTO} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import theme from 'src/theme';

interface PermissionFormModalProps {
    /** Absent: create. Present: edit — the name is fixed by then. */
    permission?: PermissionDTO;
    onClose: () => void;
}

/**
 * A catalog entry.
 *
 * Creating one switches nothing on, and the dialog says so rather than letting someone believe they
 * have just granted something: a permission is only ever consulted because a handler in the backend
 * names it. The name is therefore fixed after creation — renaming an entry the code refers to denies
 * everyone holding it, instantly and silently.
 */
export const PermissionFormModal: FC<PermissionFormModalProps> = ({permission, onClose}) => {
    const intl = useIntl();
    const [createPermission, {isLoading: creating}] = useCreatePermissionMutation();
    const [updatePermission, {isLoading: updating}] = useUpdatePermissionMutation();

    const [name, setName] = useState(permission?.name ?? '');
    // The English text, not the localized one the rest of the screen shows: this form edits the
    // source, and binding it to the translated sentence would have a German reader save their German
    // back into the English column.
    const [description, setDescription] = useState(permission?.descriptionEn ?? permission?.description ?? '');
    const [descriptionDe, setDescriptionDe] = useState(permission?.descriptionDe ?? '');
    const [descriptionFr, setDescriptionFr] = useState(permission?.descriptionFr ?? '');

    const editing = !!permission;
    const valid = /^[a-z][a-zA-Z0-9]*\.[a-z][a-zA-Z0-9]*$/.test(name.trim());

    const submit = async () => {
        try {
            if (editing) {
                await updatePermission({
                    name: permission!.name,
                    data: {name: permission!.name, description, descriptionDe, descriptionFr},
                }).unwrap();
            } else {
                await createPermission({name: name.trim(), description, descriptionDe, descriptionFr}).unwrap();
            }
            NotifySuccess(intl.formatMessage({id: 'saved', defaultMessage: 'Saved'}));
            onClose();
        } catch (error) {
            NotifyError(
                isErrorObject(error)
                    ? error.data.message
                    : intl.formatMessage({id: 'somethingWentWrong', defaultMessage: 'Something went wrong'})
            );
        }
    };

    return (
        <AMPModal
            open
            handleClose={onClose}
            title={intl.formatMessage(
                editing
                    ? {id: 'accessControlEditPermission', defaultMessage: 'Edit permission'}
                    : {id: 'accessControlCreatePermission', defaultMessage: 'New permission'}
            )}
            type="md"
            fullWidth
            withoutPaddings
        >
            <Box sx={formSx(theme)}>
                {!editing && (
                    <Alert severity="info" sx={{borderRadius: '14px'}}>
                        {intl.formatMessage({
                            id: 'accessControlPermissionInert',
                            defaultMessage:
                                'Creating an entry here does not switch anything on. A permission only takes effect once backend code checks for it by name. Until then it can be granted, and it will do nothing.',
                        })}
                    </Alert>
                )}

                <AMPInput
                    input="name"
                    type="text"
                    label="name"
                    value={name}
                    disabled={editing}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                    hasError={!editing && name.length > 0 && !valid}
                    description={intl.formatMessage({
                        id: 'accessControlPermissionNameHint',
                        defaultMessage:
                            'subject.action, for example chargers.reset. Handlers refer to permissions by name, so this cannot be changed later.',
                    })}
                />

                <AMPInput
                    input="description"
                    type="text"
                    label="description"
                    value={description}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setDescription(event.target.value)}
                    description={intl.formatMessage({
                        id: 'accessControlPermissionDescriptionHint',
                        defaultMessage:
                            'Shown wherever this permission is offered. Write it for whoever grants it, not for a developer.',
                    })}
                />

                {/* Optional, and the interface says so rather than leaving somebody to guess: left
                    blank, a reader in that language sees the English above, which is a sentence they
                    can read - unlike the bare code they would see if this text lived in the frontend
                    and the release carrying it had not shipped yet. */}
                <AMPInput
                    input="descriptionDe"
                    type="text"
                    label="accessControlPermissionDescriptionDe"
                    value={descriptionDe}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setDescriptionDe(event.target.value)}
                    description={intl.formatMessage({
                        id: 'accessControlPermissionTranslationHint',
                        defaultMessage: 'Optional. Readers in this language see the English above until it is filled in.',
                    })}
                />

                <AMPInput
                    input="descriptionFr"
                    type="text"
                    label="accessControlPermissionDescriptionFr"
                    value={descriptionFr}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setDescriptionFr(event.target.value)}
                    description={intl.formatMessage({
                        id: 'accessControlPermissionTranslationHint',
                        defaultMessage: 'Optional. Readers in this language see the English above until it is filled in.',
                    })}
                />

            </Box>

            <Box sx={actionsWrapperSx(theme)}>
                <CancelButton text="cancel" handler={onClose}/>
                <SubmitButton
                    text="save"
                    color="brand"
                    isSubmitting={creating || updating || (!editing && !valid)}
                    handler={submit}
                />
            </Box>
        </AMPModal>
    );
};
